import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getOperationalMode,
  operationalComponentSurfaces,
  operationalRadii
} from '../../../design-system/tokens';

// Estilo customizado para o Chip, garantindo consistência visual
const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'fontSize',
})(({ theme, color, variant, fontSize }) => {
  const chipTokens = operationalComponentSurfaces.chip[getOperationalMode(theme.palette.mode)];
  // Calcular altura baseada no fontSize para manter proporções
  let calculatedHeight = 28; // Altura padrão (reduzida de 32 para 28)
  if (fontSize) {
    const match = fontSize.match(/([\d.]+)(rem|px|em)?/);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = match[2] || 'rem';
      // Converter para px se necessário e calcular altura proporcional
      const fontSizeInPx = unit === 'rem' ? value * 16 : value;
      calculatedHeight = Math.max(fontSizeInPx * 2, 24); // Mínimo de 24px (reduzido de 28), proporção 2:1
    }
  }
  
  return {
    fontSize: fontSize ? `${fontSize} !important` : `calc(${theme.typography.body2.fontSize} * 0.65)`, // 65% quando não há fontSize customizado
    height: calculatedHeight,
    minHeight: calculatedHeight,
    borderRadius: `${operationalRadii.pill}px`,
    maxWidth: '100%',
    transition: 'background-color 0.18s ease, opacity 0.18s ease',
    backgroundColor: chipTokens.background,
    color: chipTokens.text,
    border: 'none',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    fontWeight: 520,
    boxShadow: 'none',
    opacity: 0.9,
    '& .MuiChip-deleteIcon': {
      color: chipTokens.text,
      opacity: 1,
      fontSize: fontSize ? `1.1em !important` : '1.1em !important',
      display: 'inline-flex !important',
      visibility: 'visible !important',
      transition: 'color 0.2s',
      '&:hover': {
        color: chipTokens.textHover,
        opacity: 1
      }
    },
    '&:hover': {
      opacity: 0.98,
      backgroundColor: chipTokens.backgroundHover,
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
    '& .MuiChip-label': {
      color: chipTokens.text,
      fontWeight: 520,
      ...(fontSize ? { fontSize: `${fontSize} !important` } : {})
    },
  };
});

const isMuiColor = (color) => [
  'default', 'primary', 'secondary', 'error', 'info', 'success', 'warning'
].includes(color);

/**
 * TagChip - Componente atômico para exibir uma tag individual
 * 
 * Este componente segue o princípio do Atomic Design como um "átomo".
 */
const TagChip = ({
  label,
  color = 'default',
  onDelete,
  size = 'medium',
  variant = 'filled',
  tooltipPlacement = 'top',
  maxLength = 20,
  sx = {},
  clickable = false,
  onClick,
  fontSize,
  ...rest
}) => {
  // Manipular clique para evitar propagação indesejada
  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  // Manipular exclusão para evitar propagação indesejada
  const handleDelete = (e) => {
    if (onDelete) {
      e.stopPropagation();
      onDelete(e);
    }
  };

  // Se o texto for maior que maxLength, trunca e adiciona reticências
  const displayLabel = typeof label === 'string' && label.length > maxLength
    ? `${label.slice(0, maxLength)}...`
    : label;
  
  // Se o texto foi truncado, adiciona um tooltip com o texto completo
  const needsTooltip = typeof label === 'string' && label.length > maxLength;
  
  // Se for cor do MUI, repassa para o Chip. Se for cor customizada, usa sx para sobrescrever.
  const chipProps = {
    label: displayLabel,
    size,
    variant,
    onDelete: onDelete ? handleDelete : undefined,
    deleteIcon: onDelete ? <CancelIcon fontSize="small" /> : undefined,
    ...(isMuiColor(color) && color !== 'default' ? { color } : {}),
    clickable: clickable || Boolean(onClick),
    onClick: onClick ? handleClick : undefined,
    sx: {
      ...sx,
    },
    ...(fontSize ? { componentsProps: { label: { style: { fontSize } } } } : {}),
    ...rest
  };

  const chipElement = <StyledChip {...chipProps} fontSize={fontSize} />;

  // Se precisa de tooltip, envolve o chip com um tooltip
  if (needsTooltip) {
    return (
      <Tooltip title={label} placement={tooltipPlacement} arrow>
        {chipElement}
      </Tooltip>
    );
  }

  // Caso contrário, retorna apenas o chip
  return chipElement;
};

TagChip.propTypes = {
  /** Texto da tag */
  label: PropTypes.string.isRequired,
  /** Cor personalizada para a tag */
  color: PropTypes.string,
  /** Função chamada ao clicar no ícone de exclusão */
  onDelete: PropTypes.func,
  /** Tamanho do chip */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Variante visual do chip */
  variant: PropTypes.oneOf(['filled', 'outlined']),
  /** Posição do tooltip */
  tooltipPlacement: PropTypes.oneOf([
    'top', 'right', 'bottom', 'left', 
    'top-start', 'top-end', 
    'bottom-start', 'bottom-end', 
    'left-start', 'left-end', 
    'right-start', 'right-end'
  ]),
  /** Comprimento máximo do texto antes de truncar */
  maxLength: PropTypes.number,
  /** Estilos adicionais */
  sx: PropTypes.object,
  /** Se o chip é clicável */
  clickable: PropTypes.bool,
  /** Função chamada ao clicar no chip */
  onClick: PropTypes.func,
  /** Tamanho de fonte customizado (opcional) */
  fontSize: PropTypes.string,
};

export default TagChip;
